/**
 * @version 1.4
 * @author Kelnik Studios {http://kelnik.ru}
 * @link https://kelnik.gitbooks.io/kelnik-documentation/content/front-end/components/utils.html documentation
 */

/**
 * DEPENDENCIES
 */
import {lock, unlock} from 'tua-body-scroll-lock';
import {DESKTOP_BREAKPOINT} from './constants';

class Utils {
    /**
     * Метод отправляет ajax запрос на сервер.
     * @param {Object} data - отправляемые данные.
     * @param {String} url - маршрут по которому нужно произвести запрос.
     * @param {Function} callback -  функция обратного вызова, которая при успехе вызовет success, а при ошибке error.
     * @param {String} method - метод отправки запроса
     */
    static send(data, url, callback = function() {}, method = 'POST') {
        const xhr = new XMLHttpRequest();
        const statusSuccess = 200;

        xhr.open(method, url);

        if (!(data instanceof FormData)) {
            xhr.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');
        }

        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

        xhr.send(data);

        xhr.onload = function XHR() {
            if (xhr.status === statusSuccess) {
                const req = JSON.parse(this.responseText);

                if (callback.success) {
                    callback.success(req);
                }
            } else if (callback.error) {
                callback.error(xhr.status);
            }

            if (callback.complete) {
                callback.complete();
            }
        };
    }

    /**
     * Метод проверяет присутствует ли ключ в объекте
     * @param {Object} object - проверяем объект
     * @param {String} key - ключ, наличие которого проверяет в объекте
     * @return {boolean} - присутствует или нет ключ в объекте
     */
    static keyExist(object, key) {
        return Object.prototype.hasOwnProperty.call(object, key);
    }

    /**
     * Метод проверяет пустой объект или нет
     * @param {Object} object - объект проверяемый на пустоту
     * @return {boolean} - true если пустой и false если полный
     */
    static isEmptyObject(object) {
        return object && Object.keys(object).length === 0 && object.constructor === Object;
    }

    /**
     * Узнает index элемента в родительской элемент
     * Аналог jquery.index()
     * @param {Node} element - искомый элемент
     * @return {number} - порядковый номер (индекс) в родительском элементе
     */
    static getElementIndex(element) {
        return Array.from(element.parentNode.children).indexOf(element);
    }

    /**
     * Проверяет, поддерживает ли устройство touch-события
     * @return {boolean} - возвращает true, если Touch-устройство, и false наоборот
     */
    static isTouch() {
        return Boolean(typeof window !== 'undefined' &&
            ('ontouchstart' in window ||
                (window.DocumentTouch &&
                    typeof document !== 'undefined' &&
                    document instanceof window.DocumentTouch))) ||
            Boolean(typeof navigator !== 'undefined' && (navigator.maxTouchPoints || navigator.msMaxTouchPoints));
    }

    /**
     * Узнает находится ли элемент во вьюпорте
     * @param {Node} element - искомый элемент
     * @return {boolean} - возвращает true, если элемент виден на экране, и false наоборот
     */
    static isInViewport(element) {
        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const windowWidth = window.innerWidth || document.documentElement.clientWidth;
        const belowViewport = 0;

        const verticalInView = (rect.top <= windowHeight) && ((rect.top + rect.height) >= belowViewport);
        const horizontalInView = (rect.left <= windowWidth) && ((rect.left + rect.width) >= belowViewport);

        return verticalInView && horizontalInView;
    }

    /**
     * Фиксирует страницу для запрета прокрутки
     * @param {Node} element - кроме данного элемента у всех остальных сбросится скролл
     */
    static bodyFixed(element) {
        lock(element, {
            overflowType: 'clip'
        });

        const padding = `${window.innerWidth - document.body.offsetWidth}px`;
        const header = document.querySelector('.header');

        if (header) {
            header.style.setProperty('--header-offset', padding);
        }
    }

    /**
     * Снимаем фиксирование страницы
     */
    static bodyStatic() {
        unlock();

        const header = document.querySelector('.header');

        if (header) {
            header.style.setProperty('--header-offset', '0px');
        }
    }

    /**
     * Метод конвертирует строку (можно с разрядами) в число
     * @param {string} str - необходимая строка
     * @return {number} - конвертированное число
     */
    static convertToNumber(str) {
        return parseFloat(str
            .toString()
            .replace(/\s/g, ''));
    }

    /**
     * Конвертирует число или строку в строку с разрядами
     * @param {string/number} value - число или строка
     * @return {string} - преобразованная строка
     */
    static convertToDigit(value) {
        return Number(value).toLocaleString('ru-Ru');
    }

    /**
     * Конвертирует целое число в дробное
     * @param {string/number} value - число или строка.
     * @param {number} denominator - целый делитель.
     * @return {number} - преобразованная строка.
     */
    static convertToRank(value, denominator) {
        const result = Number(value);

        return result / denominator;
    }

    /**
     * Генерация псевдослучайных чисел в заданном  числовом интервале
     * @param {Number} min - минимальное псевдослучайное число
     * @param {Number} max - максимальное псевдослучайное число
     * @return {Number} - возвращает псевдослучайное число в интервале [min, max]
     */
    static random(min, max) {
        return Math.floor((Math.random() * (max - min)) + min);
    }

    /**
     * Генерирует уникалькую рандомную строку из 4-х симоволов
     * @return {string}
     */
    static generateRandomValue() {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1);
    }

    /**
     * Конвертирует строку в camelCase
     * @param {string} string - необходимая строка
     * @return {string} - результат
     */
    static toCamelCase(string) {
        const dashRegEx = /-([a-z])/ig;

        return string.replace(dashRegEx, (m, letter) => {
            return letter.toUpperCase();
        });
    }

    // noinspection JSClosureCompilerSyntax
    /**
     * Возвращает дата-атрибуты из всего массива атрибутов элемента
     * Стандартный dataset не работает для svg элементов в IE.
     * @param {object} element - дом элемент
     * @return {return} - объект с дата-атрибутами
     */

    static getDataSet(element) {
        const attributes = Array.from(element.attributes);
        // eslint-disable-next-line no-useless-escape
        const dataRegEx = /^data-(.+)/;
        const dataset = {};
        const first = 1;

        attributes.forEach((item) => {
            const match = item.name.match(dataRegEx);

            if (match) {
                dataset[this.toCamelCase(match[first])] = item.value;
            }
        });

        return dataset;
    }

    /**
     * Проверяет, мобилка или нет
     * @returns {Boolean} - true - мобилка
     */
    static isMobile() {
        return Utils.getWindowWidth() < DESKTOP_BREAKPOINT;
    }

    /**
     * Выбирает окончание слова.
     * @param {number} n - количество
     * @param {array} words - массив слов. Например показать еще ['квартиру', 'квартиры', 'квартир']
     * @return {string} - слово в правильном склонении.
     */
    static pluralWord(n, words) {
        /* eslint-disable  */
        const $i = n % 10 === 1 && n % 100 !== 11 ? 0 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2;
        /* eslint-enable*/

        return words[$i];
    }

    /**
     * Возвращает булево значение если текущий размер находится в интервале переданных брейкпоинтов.
     * Например при 380 и переданных значениях (320 640) вернет true, во всех остальных случаях false.
     * @param {Number} min - минимальное значение ширины. Будет тру если больше или равно.
     * @param {Number} max - максимальное значение ширины. Будет тру если меньше.
     * @return {boolean} булево значение если попадает в переданный интервал
     */
    static isBreakpoint(min, max) {
        return window.innerWidth >= min && window.innerWidth < max;
    }

    /**
     * Проверяет является ли текущий браузер IE
     * @return {boolean} true - является
     */
    static isIE() {
        const ua = window.navigator.userAgent;
        const msie = ua.indexOf('MSIE');

        // eslint-disable-next-line
        return msie > 0 || !!navigator.userAgent.match(/Trident.*rv:11\./);
    }

    /**
     * Клик по элементу или мимо
     *
     * @param {array} selectors - Строка или массив селектор(а)ов отслеживаемых элемент(а)ов
     * @param {function} outside - Callback если клик вне элемент(а)ов
     * @param {function} inside - Callback если клик по элемент(у)ам
     */
    static clickOutside(selectors, outside = function() {}, inside = function() {}) {
        if (!selectors) {
            return;
        }

        const elements = [];

        if (typeof selectors == 'string') {
            elements.push(Array.from(document.querySelectorAll(selectors)));
        } else {
            Array.from(selectors).forEach((selector) => {
                elements.push(Array.from(document.querySelectorAll(selector)));
            });
        }

        document.addEventListener('click', (event) => {
            let target = event.target;

            do {
                try {
                    // eslint-disable-next-line no-loop-func
                    elements.forEach((element) => {
                        if (element.indexOf(target) !== -1) {
                            inside();

                            // eslint-disable-next-line no-throw-literal
                            throw false;
                        }
                    });
                } catch (error) {
                    return;
                }

                target = target.parentNode;
            } while (target);

            outside();
        });
    }

    static throttle(f, t) {
        return function (args) {
            let previousCall = this.lastCall;

            this.lastCall = Date.now();

            if (previousCall === undefined || (this.lastCall - previousCall) > t) {
                f(args);
            }
        };
    }

    static debounce(f, t) {
        return function (args) {
            let previousCall = this.lastCall;

            this.lastCall = Date.now();

            if (previousCall && ((this.lastCall - previousCall) <= t)) {
                clearTimeout(this.lastCallTimer);


                this.lastCallTimer = setTimeout(() => f(args), t);
            }

        };
    }

    static deepEqualObjects(object1, object2) {
        const keys1 = Object.keys(object1);
        const keys2 = Object.keys(object2);

        if (keys1.length !== keys2.length) {
            return false;
        }

        if ('File' in window && object1 instanceof File && object2 instanceof File) {
            for (const key in object1) {
                if (key !== 'lastModifiedDate' && object1[key] !== object2[key]) {
                    return false;
                }
            }
        }

        for (const key of keys1) {
            const val1 = object1[key];
            const val2 = object2[key];
            const areObjects = Utils.isObject(val1) && Utils.isObject(val2);
            if (areObjects && !Utils.deepEqualObjects(val1, val2) || !areObjects && val1 !== val2) {
                return false;
            }
        }

        return true;
    }

    /**
     * Сравнение значений полей объекта без сравнения их количества,
     * !важен порядок передаваемых значений!
     * @param {Object} object1 - объект-образец, сравнение по его полям
     * @param {Object} object2 - сравниваемый объект
     * @return {boolean}
     */
    static deepCompareObjects(object1, object2) {
        const keys1 = Object.keys(object1);

        for (const key of keys1) {
            const val1 = object1[key];
            const val2 = object2[key];
            const areObjects = Utils.isObject(val1) && Utils.isObject(val2);
            if (areObjects && !Utils.deepEqualObjects(val1, val2) || !areObjects && val1 !== val2) {
                return false;
            }
        }

        return true;
    }

    static isObject(object) {
        return object != null && typeof object === 'object';
    }

    /**
     * Установка cookie
     * @param name {string} - имя cookie
     * @param value {any} - значение cookie
     * @param options {object} - объект опций
     */
    static setCookie(name, value, options = {}) {
        options = {
            path: '/',
            ...options
        };

        if (options.expires instanceof Date) {
            options.expires = options.expires.toUTCString();
        }

        let updatedCookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

        for (const optionKey in options) {
            updatedCookie += `; ${optionKey}`;

            const optionValue = options[optionKey];

            if (optionValue !== true) {
                updatedCookie += `=${optionValue}`;
            }
        }
        document.cookie = updatedCookie;
    }

    static getCookie(name) {
        const matches = document.cookie.match(new RegExp(
            '(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'
        ));

        return matches ? decodeURIComponent(matches[1]) : undefined;
    }

    static deleteCookie(name) {
        Utils.setCookie(name, '', {
            'max-age': -1
        });
    }

    /**
     * Определяет ширину экрана
     * @returns {Number} Значение ширины экрана
     */
    static getWindowWidth() {
        return window.innerWidth || document.documentElement.clientWidth;
    }

    /**
     * Прокручивает страницу
     * @param {Number} y - позиция по вертикали
     * @param {Number} x - позиция по горизонтали
     */
    static scrollToPosition(y, x) {
        window.scrollTo({
            top     : y,
            left    : x,
            behavior: 'smooth'
        });
    }

    /**
     * Проверка почта на соответсвие маски/требованиям _@_.__
     * @param email - проверяемое поле
     * @return {boolean} - корректный/не корректный email
     */
    static validMail(email) {
        const re = /^[\w-\.]+@[\w-]+\.[a-z]{2,4}$/i;

        return re.test(email);
    }

    /**
     * Создаёт и открывает ссылку
     * @param url {string} - ссылка куда перенаправить
     */
    static openLink(url) {
        const link = document.createElement('a');

        link.href = url;
        document.body.appendChild(link);
        link.click();
        link.remove();
    }

    /**
     * Возвращет значение из массива объектов, по коду
     * @param arr {array} - массив объектов
     * @param name {string} - код значения
     * @return {*|number}
     */
    static getValueForName(arr, name) {
        return this.checkHasCode(arr, name) ?
            arr.find(field => field.code === name).value :
            0;
    }

    /**
     * Проверяет есть ли в массиве объектов, элемент с указанным кодом
     * @param arr {array} - массив объектов
     * @param val {string} - код значения
     * @return {*}
     */
    static checkHasCode(arr, val) {
        return arr.some(item => item.code === val);
    }

    /**
     * Формирует название помещения
     * @param room {object} - объект с данными помещения
     * @return {string} - название помещния
     */
    static getFlatName(room) {
        // id помещения с типом - квартира
        const flatId = 1;

        return room.room_type_id === flatId ?
            room.room_count + '-комнатная ' + room.type_number_string :
            room.type_number_string;
    }

    /**
     * Проверяет, выходит ли element за границу экрана слева
     * @param left
     * @return {boolean} - true - выходит | false - нет
     */
    static getOutsideElementLeft (left) {
        return Math.sign(left) === -1;
    }

    /**
     * Проверяет, выходит ли element за границу экрана слева
     * @param left
     * @param width
     * @param offset
     * @return {boolean} - true - выходит | false - нет
     */
    static getOutsideElementRight (left, width, offset = 0) {
        return window.innerWidth - (left + width + offset) <= 0;
    }

    static setPosition (params) {
        const {
            element,
            parent,
            position,
            isCenter = true,
            offsetX = 0,
            offsetY = offsetX || 0
        } = params;

        const {
            top: parentTop,
            left: parentLeft,
            width: parentWidth,
            height: parentHeight
        } = parent.getBoundingClientRect();

        const {
            clientWidth: elementWidth,
            clientHeight: elementHeight
        } = element;

        const { style } = element;
        const scrollTop = 0; // window.scrollY;

        let elementLeft = null;
        let elementTop = null;
        let elementRight = null;

        /**
         * Проверяет не выходит ли блок (element) за границы экрана по горизонтали
         * если выходит, то переопределяем параметры позиции блока
         */
        const checkOutsideHorizontal = () => {
            if (elementLeft) {
                const isOutsideLeft = Utils.getOutsideElementLeft(elementLeft);
                const isOutsideRight = Utils.getOutsideElementRight(elementLeft, elementWidth, offsetX);
                const isOutsideHorizontal = isOutsideRight || isOutsideLeft;

                if (!isOutsideHorizontal) {
                    return;
                }

                elementRight = isOutsideRight ? 0 : null;
                elementLeft = isOutsideLeft ? 0 : null;
            }
        };

        /**
         * Проверяет не выходит ли блок (element) за границы экрана по вертикали
         * если выходит, то переопределяем параметры позиции блока
         */
        const checkOutsideVertical = () => {
            const isOutsideTop = parentTop < elementHeight + offsetY;
            const isOutsideBottom = window.innerHeight - (parentTop + parentHeight) < elementHeight + offsetY;
            const isOutsideVertical = (isOutsideTop || isOutsideBottom) && position !== 'left' && position !== 'right';

            if (!isOutsideVertical) {
                return;
            }

            switch (true) {
                case !isOutsideTop && isOutsideBottom:
                    elementTop = parentTop + scrollTop - elementHeight - offsetY;
                    break;
                case isOutsideTop && !isOutsideBottom:
                    elementTop = parentTop + scrollTop + parentHeight + offsetY;
                    break;
                case isOutsideTop && isOutsideBottom:
                    elementTop = 0;
                    break;
            }
        };

        /**
         * Устанавливает параметры расположения блока (element)
         * в зависимости от заданной позиции (position)
         */
        switch (position) {
            case 'bottom':
                elementTop = parentTop + scrollTop + parentHeight + offsetY;
                elementLeft = isCenter
                    ? parentLeft - elementWidth / 2 + parentWidth / 2
                    : parentLeft + offsetX;

                break;
            case 'left':
                elementTop = isCenter
                    ? parentTop + scrollTop + parentHeight / 2 - elementHeight / 2
                    : parentTop + scrollTop + offsetY;
                elementLeft = parentLeft - elementWidth - offsetX;

                break;
            case 'right':
                elementTop = isCenter
                    ? parentTop + scrollTop + parentHeight / 2 - elementHeight / 2
                    : parentTop + scrollTop + offsetY;
                elementLeft = parentLeft + parentWidth + offsetX;

                break;
            default:
                elementTop = parentTop + scrollTop - elementHeight - offsetY;
                elementLeft = isCenter
                    ? parentLeft - elementWidth / 2 + parentWidth / 2
                    : parentLeft + offsetX;

                break;
        }

        checkOutsideHorizontal();
        checkOutsideVertical();

        style.top = elementTop !== null ? `${elementTop}px` : '';
        style.left = elementLeft !== null ? `${elementLeft}px` : '';
        style.right = elementRight !== null ? `${elementRight}px` : '';
    }

    /**
     * Удаляет элемент из body
     * @param element - элемент, который переносим
     */
    static removeBody (element) {
        element.remove();
    }

    /**
     * Переносит элемент в body
     * @param element - элемент, который переносим
     * @param parent - родитель для нового элемента.
     */
    static insertBody (element, parent) {
        const target = parent || document.body;
        target.insertBefore(element, target.lastChild);
    }

    static phoneFormat (stringPhone, plus = true) {
        const startsWith = plus ? '+7' : '8';

        let phone = stringPhone.replace(/[^0-9]/g, '');

        if (phone.startsWith('7') && plus) {
            phone = phone.substr(1);
        }

        if (phone.startsWith('8')) {
            phone = phone.substr(1);
        }

        return phone.replace(/(\d{3})(\d{3})(\d{2})(\d{2})/g, `${startsWith} ($1) $2-$3-$4`);
    }
}

export default Utils;
